import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppAvatar from "@/common/components/Avatar/AppAvatar";
import AppIconButton from "@/common/components/Button/AppIconButton";
import { InternetAndApiConnectionStatusIndicator } from "@/common/components/Connection/InternetAndApiConnectionStatusIndicator";
import WebAppAssistantActivator from "@/common/components/Entity/Assistant/AssistantActivator";
import DepartmentLink from "@/common/components/Entity/Department/DepartmentLink";
import LocationLink from "@/common/components/Entity/Location/LocationLink";
import { OnboardingHelpIconButton } from "@/common/components/Entity/Onboarding/OnboardingHelpIconButton";
import { OnboardingPopper } from "@/common/components/Entity/Onboarding/Popper/OnboardingPopper";
import TenantModeChip from "@/common/components/Entity/Tenant/TenantModeChip";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppLink from "@/common/components/Link/AppLink";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import LastNotifications from "@/common/components/Notification/LastNotifications";
import AppPopover from "@/common/components/Popover/AppPopover";
import AppPopoverContent from "@/common/components/Popover/AppPopoverContent";
import AppTypography from "@/common/components/Text/AppTypography";
import WarningsBin from "@/common/components/WarningsBin/WarningsBin";
import { THEMES } from "@/common/constants/common";
import { FeatureName } from "@/common/constants/featureName";
import { ROUTE_PATH } from "@/common/constants/routing";
import { EnvHelper } from "@/common/helpers/env";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { useCurrentTenantInfo } from "@/common/hooks/entity/tenant/useCurrentTenantInfo";
import { useAppDispatch, useAppSelector, useAppThunkDispatch } from "@/common/hooks/redux";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { tenantService } from "@/common/services/tenant";
import { NavDisplayVariants, useNavDisplay } from "@/common/useNavDisplay";
import { featureManager } from "@/config/features";
import { apiClient } from "@/core/api/ApiClient";
import * as appCommonSlice from "@/store/appCommon/slice";
import * as authSlice from "@/store/auth/slice";
import { spacing } from "@/theme";

import { AppPermission, LocationDto } from "../../../core/api/generated/v1";
import TenantCreateModal from "../TenantSelect/TenantCreateModal";

export const headerHeight = 80;

interface BaseTreeViewNode {
  id: string;
  type: "department" | "location";
  name: string;
}
interface DepartmentTreeViewNode extends BaseTreeViewNode {
  type: "department";
  locations: LocationTreeViewNode[];
}

interface LocationTreeViewNode extends BaseTreeViewNode {
  type: "location";
}

interface Props {
  mainContent?: ReactNode;
  withOnboarding?: boolean;
}

export default function BaseHeader({ mainContent, withOnboarding }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const currentTenantInfo = useCurrentTenantInfo();
  const profile = useUserProfile();
  const authorizationInfo = useAuthorizationInfo();
  const thunkDispatch = useAppThunkDispatch();
  const dispatch = useAppDispatch();
  const isNavOpened = useAppSelector((x) => x.app.isNavOpened);
  const selectedTenant = useAppSelector((x) => x.tenants.selectedTenant);

  const tenantInfo = tenantService.resolveTenant();
  const isTenantSelected = !!tenantInfo?.identifier;

  const [createTenantModalOpen, setCreateTenantModalOpen] = useState(false);

  const headerRef = useRef<HTMLDivElement>(null);

  const userDepartmentsRequest = useApiRequest(
    apiClient.departmentsApi.apiV1DepartmentsGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      departmentGetPaginatedDto: {
        ids: profile?.departments?.map((x) => x.id!) || [],
      },
    },
    {
      deps: [profile?.departments],
      skip: !profile || profile.departments?.length === 0,
    },
  );
  const userDepartments = userDepartmentsRequest.data?.items || [];

  const userLocationsRequest = useApiRequest(
    apiClient.locationsApi.apiV1LocationsGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      locationGetPaginatedDto: {
        ids: profile?.locations?.map((x) => x.id!) || [],
      },
    },
    {
      deps: [profile?.locations],
      skip: !profile || profile.locations?.length === 0,
    },
  );
  const userLocations = userLocationsRequest.data?.items || [];

  const userAffiliationTreeViewInfo = useMemo(() => {
    if (!profile) {
      return [];
    }

    const insertedLocations: LocationDto[] = [];
    const result: BaseTreeViewNode[] = userDepartments.map(
      (dep) =>
        ({
          id: dep.id,
          type: "department",
          name: dep.name,
          locations: userLocations.filter((loc) => {
            const ok = dep.locationIds?.includes(loc.id!);
            if (ok) {
              insertedLocations.push(loc);
            }
            return ok;
          }),
        }) as DepartmentTreeViewNode,
    );

    userLocations.forEach((loc) => {
      if (!insertedLocations.some((x) => x.id === loc.id)) {
        result.push({
          id: loc.id,
          type: "location",
          name: loc.name,
        } as LocationTreeViewNode);
      }
    });

    return result;
  }, [userDepartments, userLocations]);

  const renderAffiliationTreeView = useCallback(function renderNode(nodes: BaseTreeViewNode[]) {
    return nodes.map((node, index) => {
      return (
        <Stack key={index} direction='column'>
          {node.type === "department" ? (
            <DepartmentLink entityId={node.id} />
          ) : (
            <LocationLink entityId={node.id} />
          )}
          {node.type === "department" && (node as DepartmentTreeViewNode).locations.length > 0 && (
            <Stack direction='column' sx={{ pl: 2 }}>
              {renderNode((node as DepartmentTreeViewNode).locations)}
            </Stack>
          )}
        </Stack>
      );
    });
  }, []);

  const { navDisplay, navToggle } = useNavDisplay();

  useEffect(() => {
    // auto-hide initially nav on mobile
    if (isMobile) {
      if (isNavOpened) {
        thunkDispatch(appCommonSlice.hideNav());
        if (navDisplay === NavDisplayVariants.collapsed) {
          navToggle({ variant: "button", display: NavDisplayVariants.expanded });
        }
      }
    } else {
      if (!isNavOpened) {
        thunkDispatch(appCommonSlice.showNav());
      }
    }
  }, [isMobile]);

  const handleLogout = async () => {
    await thunkDispatch(authSlice.logout());
  };

  return (
    <Box
      ref={headerRef}
      data-testid='BaseHeader'
      sx={{
        width: "100%",
        height: headerHeight,
        display: "flex",
        alignItems: "center",
        flex: { xxs: `0 0 ${headerHeight}px`, md: `0 0 ${headerHeight}px` },
        gap: { xxs: 1, md: 2 },
        backgroundColor: (t) => t.palette.background.paper,
        pr: { xxs: 1, md: 1 },
        boxShadow: (t) => t.boxShadowCustom?.headerShadow,
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      {/* {!isNavOpened && ( // todo: adaptive for mobile 
        <AppIconButton
          id='nav-bar-menu-icon'
          color='secondary'
          onClick={() => {
            thunkDispatch(appCommonSlice.toggleNav());
          }}
          sx={{ mr: 1 }}
        >
          <AppIcon of='menu' />
        </AppIconButton>
      )} */}

      {/* Custom content */}
      <Box sx={{ width: "100%" }}>{mainContent}</Box>

      <Stack
        sx={{
          flexDirection: "row",
          alignItems: "center",
          gap: { xxs: 1, md: 2 },
          ml: "auto",
        }}
      >
        {/* Actions */}
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: { xxs: 0, md: 1, lg: 1.5 },
            ml: "auto",
          }}
        >
          {/* API connection status */}
          <InternetAndApiConnectionStatusIndicator />

          {/* Warnings */}
          <WarningsBin iconButtonProps={{ color: "text" }} />

          {isTenantSelected && (
            <>
              {/* Onboarding */}
              {withOnboarding && (
                <AuthorizedElement permissionsAny={[AppPermission.FleetAppAccess]}>
                  <OnboardingHelpIconButton iconButtonProps={{ color: "text" }} />

                  <OnboardingPopper
                    anchorEl={headerRef.current}
                    placement='bottom-end'
                    modifiers={[
                      {
                        name: "offset",
                        options: {
                          offset: [-(spacing * 4), spacing * 2], // [x, y]
                        },
                      },
                    ]}
                  />
                </AuthorizedElement>
              )}

              {/* Notification bell */}
              <AuthorizedElement
                permissionsAny={[
                  AppPermission.FleetAppAccess,
                  AppPermission.FleetCustomerAppAccess,
                ]}
              >
                <LastNotifications iconButtonProps={{ color: "text" }} />
              </AuthorizedElement>

              {/* AI assistant */}
              {featureManager.isEnabled(FeatureName.AiAssistant) && (
                <AuthorizedElement
                  permissionsAny={[
                    AppPermission.FleetAppAccess,
                    AppPermission.FleetCustomerAppAccess,
                  ]}
                >
                  <WebAppAssistantActivator iconButtonProps={{ color: "text" }} />
                </AuthorizedElement>
              )}
            </>
          )}
        </Stack>

        {/* Profile */}
        <Stack
          data-testid='header-profile'
          sx={{
            minWidth: 0,
            my: 1.5,
            p: (th) => th.spacing(0, 1.25),
            // backgroundColor: (t) =>
            //   t.palette.mode === "light" ? "background.gray" : "background.paper",
            // pl: selectedTenant?.name ? 1.5 : 0,
            backgroundColor: (th) => th.palette.background.paper,
            borderRadius: (th) => th.shapeCustom.borderRadiusButton,
            boxShadow: (th) => th.shadows[5],

            "&:hover": {
              backgroundColor: (th) => th.palette.background.paperHover,
            },
          }}
          direction='row'
          alignItems='center'
        >
          {currentTenantInfo?.identifier && (
            <Stack
              sx={{
                display: { xxs: "none", sm: "initial" },
                flexDirection: "column",
                alignItems: "flex-start",
                minWidth: 0,
                maxWidth: { xxs: "100px", md: "max-content" },
                mr: 1.25,
                overflow: "hidden",
              }}
            >
              {/* Current tenant */}
              <AppLink
                sx={{ width: "max-content" }}
                underline='none'
                to={ROUTE_PATH.SELECT_TENANT}
                disableLinkStyles
              >
                <AppPopover
                  hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
                  trigger={
                    <AppTypography component='span' variant='subtitle2'>
                      {selectedTenant?.name || currentTenantInfo?.identifier}
                    </AppTypography>
                  }
                >
                  <AppPopoverContent
                    sx={{ minWidth: 140, maxWidth: 300, minHeight: 20, maxHeight: 300 }}
                  >
                    {/* Department and locations */}
                    <Stack>
                      <AppTypography variant='subtitle2'>My affiliation</AppTypography>
                      {renderAffiliationTreeView(userAffiliationTreeViewInfo)}
                    </Stack>
                  </AppPopoverContent>
                </AppPopover>
              </AppLink>

              <TenantModeChip
                sx={{ width: { xxs: "100%", md: "min-content" } }}
                mode={selectedTenant?.mode}
              />
            </Stack>
          )}

          {/* Main header menu */}
          <MenuWithTrigger
            withAuthCloseOnClick
            trigger={
              <AppIconButton
                shape='square'
                sx={{
                  // ml: selectedTenant?.name ? 0.5 : 0,
                  my: 1,
                  p: 0,
                  backgroundColor: "transparent",
                }}
              >
                <AppAvatar
                  sx={{
                    fontSize: (t) => t.typography.pxToRem(16),
                    fontWeight: 700,
                    backgroundColor: (th) => th.palette.secondary.main,
                  }}
                  profile={profile}
                  size={40}
                />
              </AppIconButton>
            }
            slotProps={{
              paper: {
                sx: {
                  maxWidth: 300,
                  width: 300,
                  borderRadius: (t) => t.shapeCustom.borderRadiusCard,
                },
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                px: 3,
                pt: 1,
                pb: 4,
                textAlign: "center",
              }}
            >
              <AppAvatar sx={{ mb: 1 }} profile={profile} size={56} />
              <AppTypography variant='subtitle2'>{profile?.personName?.name}</AppTypography>
              <AppTypography sx={{ fontSize: (t) => t.typography.pxToRem(12) }}>
                {profile?.email}
              </AppTypography>
            </Box>

            <MenuItem
              sx={{ minWidth: "200px" }}
              component={RouterLink}
              to={
                ((authorizationInfo.hasFleetAppAccess ||
                  authorizationInfo.hasFleetCustomerAppAccess) &&
                  ROUTE_PATH.FLEET_PROFILE_VIEW) ||
                // (authorizationInfo.hasFleetCustomerAppAccess && ROUTE_PATH.FLEET_CUSTOMER_PROFILE) ||
                ROUTE_PATH.PROFILE
              }
            >
              <ListItemIcon>
                <AppIcon of='profile' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>

            {!EnvHelper.isProductionAny && (
              <MenuItem
                sx={{ minWidth: "200px" }}
                onClick={() =>
                  dispatch(
                    appCommonSlice._themeChange(
                      theme.palette.mode === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT,
                    ),
                  )
                }
              >
                <ListItemIcon>
                  <AppIcon of='theme' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Appearance</ListItemText>
                <Switch
                  sx={{
                    my: -1,
                  }}
                  checked={theme.palette.mode === THEMES.LIGHT}
                />
              </MenuItem>
            )}

            <Divider />

            <AuthorizedElement
              permissions={[AppPermission.FleetAppAccess, AppPermission.TenantManage]}
            >
              <MenuItem
                sx={{ minWidth: "200px" }}
                component={RouterLink}
                to={ROUTE_PATH.MANAGEMENT_TENANT_PROFILE}
              >
                <ListItemIcon>
                  <AppIcon of='companyProfile' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Company profile</ListItemText>
              </MenuItem>
            </AuthorizedElement>

            <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
              <MenuItem
                sx={{ minWidth: "200px" }}
                component={RouterLink}
                to={ROUTE_PATH.SELECT_TENANT}
              >
                <ListItemIcon>
                  <AppIcon of='switchTenant' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Switch company</ListItemText>
              </MenuItem>
            </AuthorizedElement>

            <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
              <MenuItem sx={{ minWidth: "200px" }} onClick={() => setCreateTenantModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='createNewTenant' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Create new company</ListItemText>
              </MenuItem>
            </AuthorizedElement>

            <Divider />

            <MenuItem onClick={handleLogout} sx={{ minWidth: "200px" }}>
              <ListItemIcon>
                <AppIcon of='logout' fontSize='small' color='error' />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </MenuWithTrigger>
        </Stack>
      </Stack>

      {/* Create tenant modal */}
      {createTenantModalOpen && (
        <AuthorizedElement permissions={[]}>
          <TenantCreateModal
            open={createTenantModalOpen}
            onClose={() => setCreateTenantModalOpen(false)}
            onCompanyCreated={() => setCreateTenantModalOpen(false)}
          />
        </AuthorizedElement>
      )}
    </Box>
  );
}
